import dayjs from 'dayjs';

import {
  AvailabilityPeriodEnum,
  Furnishings,
  ListingRequest,
  RentalPriceID,
  Statuses,
  SurfaceMinimumID,
} from '@kamernet/core/ApiClient/Kamernet';
import {
  ListingsSearchCategory,
  mapListinsSearchCategoriesToListingTypes,
} from '@kamernet/core/Routing/routes/ListingsSearch';

import { ListingsSearchAllFilters } from '../types';

import { isCitySearch } from './isCitySearch';
import { isStreetSearch } from './isStreetSearch';

const studentHouseMaxRentByCityName: Record<string, RentalPriceID> = {
  Groningen: RentalPriceID.Eur500,
  Rotterdam: RentalPriceID.Eur600,
};

export function mapListingsSearchAllFiltersToListingRequest(
  allFiltersOriginal: ListingsSearchAllFilters,
): ListingRequest {
  const allFilters = JSON.parse(
    JSON.stringify(allFiltersOriginal),
  ) as ListingsSearchAllFilters;

  const isCityOrStreetSearch =
    isCitySearch(allFilters) || isStreetSearch(allFilters);

  const searchCategories = allFilters.searchCategories || [];
  const furnishings = allFilters.furnishings || [];
  const availabilityPeriods = allFilters.availabilityPeriods || [];
  const suitableForWorkStatuses = allFilters.suitableForWorkStatuses || [];
  let { maxRent } = allFilters;

  if (
    searchCategories.includes(ListingsSearchCategory.FurnishedApartment) &&
    !furnishings.includes(Furnishings.Furnished)
  ) {
    furnishings.push(Furnishings.Furnished);
  }

  if (
    searchCategories.includes(ListingsSearchCategory.ShortTermRental) &&
    !availabilityPeriods.includes(AvailabilityPeriodEnum.Temporary)
  ) {
    availabilityPeriods.push(AvailabilityPeriodEnum.Temporary);
  }

  if (
    searchCategories.includes(ListingsSearchCategory.LongTermRental) &&
    !availabilityPeriods.includes(AvailabilityPeriodEnum.Indefinite)
  ) {
    availabilityPeriods.push(AvailabilityPeriodEnum.Indefinite);
  }

  if (searchCategories.includes(ListingsSearchCategory.StudentHousing)) {
    if (!suitableForWorkStatuses.includes(Statuses.Student)) {
      suitableForWorkStatuses.push(Statuses.Student);
    }

    if (!suitableForWorkStatuses.includes(Statuses.WorkingStudent)) {
      suitableForWorkStatuses.push(Statuses.WorkingStudent);
    }

    if (maxRent === RentalPriceID.Empty && isCityOrStreetSearch) {
      const studentHouseMaxRent =
        studentHouseMaxRentByCityName[allFilters.cityName];

      if (studentHouseMaxRent) {
        maxRent = studentHouseMaxRent;
      }
    }
  }

  let availableFrom: string | null = null;

  if (allFilters.availableFrom) {
    availableFrom = dayjs(allFilters.availableFrom)
      .format('YYYY-MM-DD')
      .toString();
  }

  const listingRequest: ListingRequest = {
    location: isCityOrStreetSearch
      ? {
          name: allFilters.cityName,
          cityName: allFilters.cityName,
          citySlug: allFilters.citySlug,
        }
      : null,
    streetSlug: allFilters.streetSlug || null,
    citySlug: allFilters.citySlug || null,
    streetName: allFilters.streetName || null,
    radiusId: allFilters.radius,
    listingTypeIds: mapListinsSearchCategoriesToListingTypes(
      allFilters.searchCategories,
    ),
    maxRentalPriceId: maxRent,
    surfaceMinimumId: allFilters.minSize || SurfaceMinimumID.M6,
    listingSortOptionId: allFilters.sort,
    pageNo: allFilters.pageNo,
    suitableForGenderIds: allFilters.suitableForGenders,
    furnishings,
    availabilityPeriods,
    availableFromDate: availableFrom,
    isBathroomPrivate: allFilters.isBathroomPrivate || null,
    isToiletPrivate: allFilters.isToiletPrivate || null,
    isKitchenPrivate: allFilters.isKitchenPrivate || null,
    hasInternet: allFilters.hasInternet || null,
    suitableForNumberOfPersonsId: allFilters.suitableForNumberOfPersons || null,
    candidateAge: allFilters.suitableForAge || null,
    suitableForStatusIds: allFilters.suitableForWorkStatuses,
    isSmokingInsideAllowed: allFilters.isSmokingInsideAllowed || null,
    isPetsInsideAllowed: allFilters.isPetsInsideAllowed || null,
    roommateMaxNumberId: allFilters.roommateNumberMax || null,
    roommateGenderIds: allFilters.roommateGenders,
    ownerTypeIds: allFilters.ownerTypes,
    variant: '',
    searchview: allFilters.searchView,
    topAdsOnly: null,
    topAdOffset: null,
    rowsPerPage: allFilters.listingsPerPage,
    lat_lo: allFilters.selat,
    lng_lo: allFilters.nwlng,
    lat_hi: allFilters.nwlat,
    lng_hi: allFilters.selng,
  };

  return listingRequest;
}
